import { Badge } from "@babackapp/customer-ui/components/badge";
import { Label } from "@babackapp/customer-ui/components/label";
import {
  Option,
  OptionAction,
  OptionDescription,
  OptionFooter,
  OptionFooterBanner,
  OptionIcon,
  OptionsList,
  OptionTitle,
} from "@babackapp/customer-ui/components/option";
import {
  RadioGroup,
  RadioGroupItem,
} from "@babackapp/customer-ui/components/radio-group";
import type { ResolutionOption } from "@babackapp/return-widget-components/types";
import { StarFilledIcon } from "@radix-ui/react-icons";
import { Receipt, RefreshCcw, WalletIcon } from "lucide-react";
import { AnimatePresence, motion, type Variants } from "motion/react";

type Props = {
  value: ResolutionOption | null;
  onChange: (value: ResolutionOption) => void;
};

export function RefundOptions({ value, onChange }: Props) {
  return (
    <OptionsList>
      <RadioGroup
        value={value ?? ""}
        onValueChange={(v) => onChange(v as ResolutionOption)}
      >
        <DiscountOption />
        <BankTransferOption selected={value === "bank-transfer"} />
      </RadioGroup>
    </OptionsList>
  );
}

function DiscountOption() {
  return (
    <Label htmlFor="discount-type">
      <Option>
        <OptionIcon>
          <RefreshCcw />
        </OptionIcon>
        <OptionTitle>
          Bon de réduction
          <Badge variant="recomended">
            Recommendé <StarFilledIcon />
          </Badge>
        </OptionTitle>
        <OptionDescription>
          Recevez un bon de réduction à utiliser sur votre prochaine commande.
        </OptionDescription>
        <OptionAction>
          <RadioGroupItem value="discount-code" id="discount-type" />
        </OptionAction>
      </Option>
    </Label>
  );
}

const footerVariants: Variants = {
  hidden: { height: 0, transition: { duration: 0.2 } },
  visible: {
    height: "auto",
    transition: { ease: [0.25, 0.1, 0.25, 1], duration: 0.25, delay: 0.2 },
  },
};

const footerContentVariants: Variants = {
  hidden: { opacity: 0, rotateX: -60, y: 10 },
  visible: {
    opacity: 1,
    rotateX: 0,
    y: 0,
    transition: { type: "spring", delay: 0.3 },
  },
};

function BankTransferOption({ selected }: { selected: boolean }) {
  return (
    <Label htmlFor="refund-type">
      <Option>
        <OptionIcon>
          <Receipt />
        </OptionIcon>
        <OptionTitle>Remboursement bancaire</OptionTitle>
        <OptionDescription>
          Recevez un remboursement directement sur votre compte en banque.
        </OptionDescription>
        <OptionAction>
          <RadioGroupItem value="bank-transfer" id="refund-type" />
        </OptionAction>
        <OptionFooter className="[perspective:600px]">
          <AnimatePresence>
            {selected && (
              <motion.div
                variants={footerVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className="mt-2"
              >
                <motion.div
                  variants={footerContentVariants}
                  className="origin-top"
                >
                  <OptionFooterBanner>
                    <WalletIcon className="size-4" />
                    <span className="flex-1">
                      Certain ? Gagnez un bonus en choisissant un bon de
                      réduction.
                    </span>
                  </OptionFooterBanner>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </OptionFooter>
      </Option>
    </Label>
  );
}
